// Admin API client
const ADMIN_API_BASE_URL = import.meta.env.VITE_ADMIN_API_BASE_URL || "";

interface ApiResponse<T> {
  data?: T;
  error?: string;
}

class AdminApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    if (typeof window !== "undefined") {
      this.token = localStorage.getItem("auth_token");
    }
  }

  setToken(token: string | null) {
    this.token = token;
    if (token && typeof window !== "undefined") {
      localStorage.setItem("auth_token", token);
    } else if (typeof window !== "undefined") {
      localStorage.removeItem("auth_token");
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    useFullUrl: boolean = false
  ): Promise<ApiResponse<T>> {
    const url = useFullUrl ? endpoint : `${this.baseUrl}${endpoint}`;
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...options.headers,
    };

    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let error;
        try {
          error = JSON.parse(errorText);
        } catch {
          error = { error: errorText || `HTTP ${response.status}` };
        }
        return { error: error.error || `HTTP ${response.status}` };
      }

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Failed to parse response as JSON:", responseText);
        return { error: "Invalid JSON response from server" };
      }
      return { data };
    } catch (error) {
      console.error("Request error:", error);
      return { error: error instanceof Error ? error.message : "Network error" };
    }
  }

  // Dashboard
  async getDashboard(days: number = 30) {
    const DASHBOARD_URL = import.meta.env.VITE_ADMIN_DASHBOARD_URL || "";
    const url = DASHBOARD_URL || `${this.baseUrl}/admin/dashboard`;
    return this.request<{
      overview: any;
      funnel: any;
      sales: any;
      recentActivity: any;
      timeRange: any;
    }>(`${url}?days=${days}`, {}, !!DASHBOARD_URL);
  }

  // Admin-configurable app settings (feature flags)
  async getConfig() {
    const CONFIG_URL = import.meta.env.VITE_ADMIN_CONFIG_URL || "";
    const url = CONFIG_URL || `${this.baseUrl}/admin/config`;
    return this.request<{ config: { communityEnabled: boolean } }>(url, {}, !!CONFIG_URL);
  }

  async updateConfig(payload: { communityEnabled?: boolean }) {
    const CONFIG_URL = import.meta.env.VITE_ADMIN_CONFIG_URL || "";
    const url = CONFIG_URL || `${this.baseUrl}/admin/config`;
    return this.request<{ config: { communityEnabled: boolean } }>(
      url,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
      !!CONFIG_URL
    );
  }

  // Leadership Articles
  async listLeadershipArticles() {
    const ARTICLES_URL = import.meta.env.VITE_ADMIN_LEADERSHIP_ARTICLES_URL || "";
    const url = ARTICLES_URL || `${this.baseUrl}/admin/leadership-articles`;
    return this.request<{ articles: any[] }>(url, {}, !!ARTICLES_URL);
  }

  async generateLeadershipArticle(payload: {
    prompt: string;
    sourceUrl?: string;
    category?: string;
    tag?: string;
  }) {
    const GENERATE_URL = import.meta.env.VITE_ADMIN_GENERATE_LEADERSHIP_ARTICLE_URL || "";
    const url = GENERATE_URL || `${this.baseUrl}/admin/leadership-articles/generate`;
    return this.request<{ article: any }>(
      url,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
      !!GENERATE_URL
    );
  }

  async createLeadershipArticle(payload: {
    title: string;
    slug?: string;
    description: string;
    hook: string;
    readTime: string;
    date: string;
    category: string;
    tag: string;
    image: string;
    takeaways: string[];
    content: string;
    isFeatured?: boolean;
  }) {
    const ARTICLES_URL = import.meta.env.VITE_ADMIN_LEADERSHIP_ARTICLES_URL || "";
    const url = ARTICLES_URL || `${this.baseUrl}/admin/leadership-articles`;
    return this.request<{ article: any }>(
      url,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
      !!ARTICLES_URL
    );
  }

  async deleteLeadershipArticle(id: string) {
    const ARTICLES_URL = import.meta.env.VITE_ADMIN_LEADERSHIP_ARTICLES_URL || "";
    const url = ARTICLES_URL || `${this.baseUrl}/admin/leadership-articles`;
    return this.request<{ message: string }>(`${url}?id=${id}`, { method: "DELETE" }, !!ARTICLES_URL);
  }

  async updateLeadershipArticle(id: string, payload: Record<string, any>) {
    const ARTICLES_URL = import.meta.env.VITE_ADMIN_LEADERSHIP_ARTICLES_URL || "";
    const url = ARTICLES_URL || `${this.baseUrl}/admin/leadership-articles`;
    return this.request<{ article: any }>(
      `${url}?id=${id}`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
      !!ARTICLES_URL
    );
  }

  // Users
  async getUsers() {
    const USERS_URL = import.meta.env.VITE_ADMIN_USERS_URL || "";
    const url = USERS_URL || `${this.baseUrl}/admin/users`;
    return this.request<{ users: any[] }>(url, {}, !!USERS_URL);
  }

  // Students (progress + activity)
  async getStudents(courseId?: string) {
    const STUDENTS_URL = import.meta.env.VITE_ADMIN_STUDENTS_URL || "";
    const baseUrl = STUDENTS_URL || `${this.baseUrl}/admin/students`;
    const url = courseId ? `${baseUrl}?courseId=${encodeURIComponent(courseId)}` : baseUrl;
    return this.request<{ students: any[] }>(url, {}, !!STUDENTS_URL);
  }

  async getStudent(userId: string) {
    const STUDENTS_URL = import.meta.env.VITE_ADMIN_STUDENTS_URL || "";
    const baseUrl = STUDENTS_URL || `${this.baseUrl}/admin/students`;
    const url = `${baseUrl}?userId=${encodeURIComponent(userId)}`;
    return this.request<any>(url, {}, !!STUDENTS_URL);
  }

  // Questions (submitted by students)
  async getQuestions(userId?: string, options?: { includeArchived?: boolean }) {
    const QUESTIONS_URL = import.meta.env.VITE_ADMIN_QUESTIONS_URL || "";
    const baseUrl = QUESTIONS_URL || `${this.baseUrl}/admin/questions`;
    const params = new URLSearchParams();
    if (userId) params.set("userId", userId);
    if (options?.includeArchived) params.set("includeArchived", "1");
    const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
    return this.request<{ questions: any[] }>(url, {}, !!QUESTIONS_URL);
  }

  async updateQuestion(payload: {
    userId: string;
    id: string;
    answered?: boolean;
    answeredComment?: string;
    archived?: boolean;
  }) {
    const QUESTIONS_URL = import.meta.env.VITE_ADMIN_QUESTIONS_URL || "";
    const url = QUESTIONS_URL || `${this.baseUrl}/admin/questions`;
    return this.request<{ question: any }>(
      url,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
      !!QUESTIONS_URL
    );
  }

  async getUser(userId: string) {
    const USERS_URL = import.meta.env.VITE_ADMIN_USERS_URL || "";
    const baseUrl = USERS_URL || `${this.baseUrl}/admin/users`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ user: any }>(`${url}/${userId}`, {}, !!USERS_URL);
  }

  async createUser(userData: any) {
    const USERS_URL = import.meta.env.VITE_ADMIN_USERS_URL || "";
    const url = USERS_URL || `${this.baseUrl}/admin/users`;
    return this.request<{ user: any }>(url, {
      method: "POST",
      body: JSON.stringify(userData),
    }, !!USERS_URL);
  }

  async updateUser(userId: string, userData: any) {
    const USERS_URL = import.meta.env.VITE_ADMIN_USERS_URL || "";
    const baseUrl = USERS_URL || `${this.baseUrl}/admin/users`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ user: any }>(`${url}/${userId}`, {
      method: "PUT",
      body: JSON.stringify(userData),
    }, !!USERS_URL);
  }

  async deleteUser(userId: string) {
    const USERS_URL = import.meta.env.VITE_ADMIN_USERS_URL || "";
    const baseUrl = USERS_URL || `${this.baseUrl}/admin/users`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ message: string }>(`${url}/${userId}`, {
      method: "DELETE",
    }, !!USERS_URL);
  }

  // Leads
  async getLeads() {
    const LEADS_URL = import.meta.env.VITE_ADMIN_LEADS_URL || "";
    const url = LEADS_URL || `${this.baseUrl}/admin/leads`;
    return this.request<{ leads: any[] }>(url, {}, !!LEADS_URL);
  }

  async exportLeadsCsv() {
    const LEADS_URL = import.meta.env.VITE_ADMIN_LEADS_URL || "";
    const baseUrl = LEADS_URL || `${this.baseUrl}/admin/leads`;
    const url = `${baseUrl}?format=csv`;
    const headers: HeadersInit = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || "Failed to export leads");
    }
    return response.blob();
  }

  async deleteLeads(ids: string[]) {
    const LEADS_URL = import.meta.env.VITE_ADMIN_LEADS_URL || "";
    const url = LEADS_URL || `${this.baseUrl}/admin/leads`;
    return this.request<{ message: string }>(url, {
      method: "POST",
      body: JSON.stringify({ action: "delete-bulk", ids })
    }, !!LEADS_URL);
  }

  async deleteLead(leadId: string) {
    const LEADS_URL = import.meta.env.VITE_ADMIN_LEADS_URL || "";
    const baseUrl = LEADS_URL || `${this.baseUrl}/admin/leads`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ message: string }>(`${url}/${leadId}`, {
      method: "DELETE",
    }, !!LEADS_URL);
  }

  // Courses
  async getCourses() {
    const COURSES_URL = import.meta.env.VITE_ADMIN_COURSES_URL || "";
    const url = COURSES_URL || `${this.baseUrl}/admin/courses`;
    return this.request<{ courses: any[] }>(url, {}, !!COURSES_URL);
  }

  // Modules
  async getModules(courseId?: string) {
    const MODULES_URL = import.meta.env.VITE_ADMIN_MODULES_URL || "";
    const url = MODULES_URL || `${this.baseUrl}/admin/modules`;
    const query = courseId ? `?courseId=${courseId}` : "";
    return this.request<{ modules: any[] }>(`${url}${query}`, {}, !!MODULES_URL);
  }

  async getModule(moduleId: string) {
    const MODULES_URL = import.meta.env.VITE_ADMIN_MODULES_URL || "";
    const baseUrl = MODULES_URL || `${this.baseUrl}/admin/modules`;
    // Remove trailing slash to avoid double slashes
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ module: any; lessons: any[] }>(`${url}/${moduleId}`, {}, !!MODULES_URL);
  }

  async createModule(moduleData: any) {
    const MODULES_URL = import.meta.env.VITE_ADMIN_MODULES_URL || "";
    const url = MODULES_URL || `${this.baseUrl}/admin/modules`;
    return this.request<{ module: any }>(url, {
      method: "POST",
      body: JSON.stringify(moduleData),
    }, !!MODULES_URL);
  }

  async updateModule(moduleId: string, moduleData: any) {
    const MODULES_URL = import.meta.env.VITE_ADMIN_MODULES_URL || "";
    const baseUrl = MODULES_URL || `${this.baseUrl}/admin/modules`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ module: any }>(`${url}/${moduleId}`, {
      method: "PUT",
      body: JSON.stringify(moduleData),
    }, !!MODULES_URL);
  }

  async deleteModule(moduleId: string) {
    const MODULES_URL = import.meta.env.VITE_ADMIN_MODULES_URL || "";
    const baseUrl = MODULES_URL || `${this.baseUrl}/admin/modules`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ message: string }>(`${url}/${moduleId}`, {
      method: "DELETE",
    }, !!MODULES_URL);
  }

  // Lessons
  async getLessons(moduleId?: string) {
    const LESSONS_URL = import.meta.env.VITE_ADMIN_LESSONS_URL || "";
    const url = LESSONS_URL || `${this.baseUrl}/admin/lessons`;
    const query = moduleId ? `?moduleId=${moduleId}` : "";
    return this.request<{ lessons: any[] }>(`${url}${query}`, {}, !!LESSONS_URL);
  }

  async getLesson(moduleId: string, lessonId: string) {
    const LESSONS_URL = import.meta.env.VITE_ADMIN_LESSONS_URL || "";
    const baseUrl = LESSONS_URL || `${this.baseUrl}/admin/lessons`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ lesson: any }>(`${url}/${lessonId}?moduleId=${moduleId}`, {}, !!LESSONS_URL);
  }

  async createLesson(lessonData: any) {
    const LESSONS_URL = import.meta.env.VITE_ADMIN_LESSONS_URL || "";
    const url = LESSONS_URL || `${this.baseUrl}/admin/lessons`;
    return this.request<{ lesson: any }>(url, {
      method: "POST",
      body: JSON.stringify(lessonData),
    }, !!LESSONS_URL);
  }

  async updateLesson(moduleId: string, lessonId: string, lessonData: any) {
    const LESSONS_URL = import.meta.env.VITE_ADMIN_LESSONS_URL || "";
    const baseUrl = LESSONS_URL || `${this.baseUrl}/admin/lessons`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ lesson: any }>(`${url}/${lessonId}?moduleId=${moduleId}`, {
      method: "PUT",
      body: JSON.stringify(lessonData),
    }, !!LESSONS_URL);
  }

  async deleteLesson(moduleId: string, lessonId: string) {
    const LESSONS_URL = import.meta.env.VITE_ADMIN_LESSONS_URL || "";
    const baseUrl = LESSONS_URL || `${this.baseUrl}/admin/lessons`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ message: string }>(`${url}/${lessonId}?moduleId=${moduleId}`, {
      method: "DELETE",
    }, !!LESSONS_URL);
  }

  // Newsletter
  async getSubscribers() {
    const NEWSLETTER_URL = import.meta.env.VITE_ADMIN_NEWSLETTER_URL || "";
    const url = NEWSLETTER_URL || `${this.baseUrl}/admin/newsletter`;
    return this.request<{ subscribers: any[]; stats: any }>(url, {}, !!NEWSLETTER_URL);
  }

  async exportSubscribersCsv() {
    const NEWSLETTER_URL = import.meta.env.VITE_ADMIN_NEWSLETTER_URL || "";
    const baseUrl = NEWSLETTER_URL || `${this.baseUrl}/admin/newsletter`;
    const url = `${baseUrl}?format=csv`;
    const headers: HeadersInit = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || "Failed to export subscribers");
    }
    return response.blob();
  }

  async createSubscriber(subscriberData: any) {
    const NEWSLETTER_URL = import.meta.env.VITE_ADMIN_NEWSLETTER_URL || "";
    const url = NEWSLETTER_URL || `${this.baseUrl}/admin/newsletter`;
    return this.request<{ subscriber: any }>(url, {
      method: "POST",
      body: JSON.stringify(subscriberData),
    }, !!NEWSLETTER_URL);
  }

  async updateSubscriber(subscriberId: string, subscriberData: any) {
    const NEWSLETTER_URL = import.meta.env.VITE_ADMIN_NEWSLETTER_URL || "";
    const baseUrl = NEWSLETTER_URL || `${this.baseUrl}/admin/newsletter`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ subscriber: any }>(`${url}/${subscriberId}`, {
      method: "PUT",
      body: JSON.stringify(subscriberData),
    }, !!NEWSLETTER_URL);
  }

  async resendNewsletterVerification(subscriberId: string) {
    const NEWSLETTER_URL = import.meta.env.VITE_ADMIN_NEWSLETTER_URL || "";
    const baseUrl = NEWSLETTER_URL || `${this.baseUrl}/admin/newsletter`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ message: string }>(`${url}/${subscriberId}`, {
      method: "POST",
      body: JSON.stringify({ action: "resend-verification" }),
    }, !!NEWSLETTER_URL);
  }

  async deleteSubscriber(subscriberId: string) {
    const NEWSLETTER_URL = import.meta.env.VITE_ADMIN_NEWSLETTER_URL || "";
    const baseUrl = NEWSLETTER_URL || `${this.baseUrl}/admin/newsletter`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ message: string }>(`${url}/${subscriberId}`, {
      method: "DELETE",
    }, !!NEWSLETTER_URL);
  }

  // Resources
  async getResources(courseId?: string) {
    const RESOURCES_URL = import.meta.env.VITE_ADMIN_RESOURCES_URL || "";
    const url = RESOURCES_URL || `${this.baseUrl}/admin/resources`;
    const query = courseId ? `?courseId=${courseId}` : "";
    return this.request<{ resources: any[] }>(`${url}${query}`, {}, !!RESOURCES_URL);
  }

  async getResource(resourceId: string) {
    const RESOURCES_URL = import.meta.env.VITE_ADMIN_RESOURCES_URL || "";
    const baseUrl = RESOURCES_URL || `${this.baseUrl}/admin/resources`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ resource: any }>(`${url}/${resourceId}`, {}, !!RESOURCES_URL);
  }

  async createResource(resourceData: any) {
    const RESOURCES_URL = import.meta.env.VITE_ADMIN_RESOURCES_URL || "";
    const url = RESOURCES_URL || `${this.baseUrl}/admin/resources`;
    return this.request<{ resource: any }>(url, {
      method: "POST",
      body: JSON.stringify(resourceData),
    }, !!RESOURCES_URL);
  }

  async updateResource(resourceId: string, resourceData: any) {
    const RESOURCES_URL = import.meta.env.VITE_ADMIN_RESOURCES_URL || "";
    const baseUrl = RESOURCES_URL || `${this.baseUrl}/admin/resources`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ resource: any }>(`${url}/${resourceId}`, {
      method: "PUT",
      body: JSON.stringify(resourceData),
    }, !!RESOURCES_URL);
  }

  async deleteResource(resourceId: string) {
    const RESOURCES_URL = import.meta.env.VITE_ADMIN_RESOURCES_URL || "";
    const baseUrl = RESOURCES_URL || `${this.baseUrl}/admin/resources`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ message: string }>(`${url}/${resourceId}`, {
      method: "DELETE",
    }, !!RESOURCES_URL);
  }

  // Upload
  async getUploadUrl(fileName: string, fileType: string, folder?: string) {
    const UPLOAD_URL = import.meta.env.VITE_ADMIN_UPLOAD_URL || "";
    const url = UPLOAD_URL || `${this.baseUrl}/admin/upload`;
    return this.request<{ uploadUrl: string; fileUrl: string; fileKey: string; fileName: string }>(
      url,
      {
        method: "POST",
        body: JSON.stringify({ fileName, fileType, folder }),
      },
      !!UPLOAD_URL
    );
  }

  // Sessions
  async getSessions(courseId?: string) {
    const SESSIONS_URL = import.meta.env.VITE_ADMIN_SESSIONS_URL || "";
    const url = SESSIONS_URL || `${this.baseUrl}/admin/sessions`;
    const query = courseId ? `?courseId=${courseId}` : "";
    return this.request<{ sessions: any[] }>(`${url}${query}`, {}, !!SESSIONS_URL);
  }

  async getSession(sessionId: string) {
    const SESSIONS_URL = import.meta.env.VITE_ADMIN_SESSIONS_URL || "";
    const baseUrl = SESSIONS_URL || `${this.baseUrl}/admin/sessions`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ session: any }>(`${url}/${sessionId}`, {}, !!SESSIONS_URL);
  }

  async createSession(sessionData: any) {
    const SESSIONS_URL = import.meta.env.VITE_ADMIN_SESSIONS_URL || "";
    const url = SESSIONS_URL || `${this.baseUrl}/admin/sessions`;
    return this.request<{ session: any }>(url, {
      method: "POST",
      body: JSON.stringify(sessionData),
    }, !!SESSIONS_URL);
  }

  async updateSession(sessionId: string, sessionData: any) {
    const SESSIONS_URL = import.meta.env.VITE_ADMIN_SESSIONS_URL || "";
    const baseUrl = SESSIONS_URL || `${this.baseUrl}/admin/sessions`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ session: any }>(`${url}/${sessionId}`, {
      method: "PUT",
      body: JSON.stringify(sessionData),
    }, !!SESSIONS_URL);
  }

  async deleteSession(sessionId: string) {
    const SESSIONS_URL = import.meta.env.VITE_ADMIN_SESSIONS_URL || "";
    const baseUrl = SESSIONS_URL || `${this.baseUrl}/admin/sessions`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{ message: string }>(`${url}/${sessionId}`, {
      method: "DELETE",
    }, !!SESSIONS_URL);
  }

  // Video Processing (AI)
  async processVideo(videoUrl: string) {
    const PROCESS_VIDEO_URL = import.meta.env.VITE_ADMIN_PROCESS_VIDEO_URL || "";
    const baseUrl = PROCESS_VIDEO_URL || `${this.baseUrl}/admin/process-video`;
    const url = baseUrl.replace(/\/+$/, "");
    return this.request<{
      videoUrl: string;
      chapters: Array<{ time: number; title: string; summary: string }>;
      transcript: Array<{ time: number; speaker: string; text: string }>;
      keyTakeaways: string[];
      reflectionPrompts: string[];
      summary?: string;
    }>(
      url,
      {
        method: "POST",
        body: JSON.stringify({ videoUrl }),
      },
      !!PROCESS_VIDEO_URL
    );
  }

  // Leadership diagnostics (Activities)
  async getActivities(courseId?: string) {
    const ACTIVITIES_URL = import.meta.env.VITE_ADMIN_ACTIVITIES_URL || "";
    const url = ACTIVITIES_URL || `${this.baseUrl}/admin/activities`;
    const query = courseId ? `?courseId=${courseId}` : "";
    return this.request<{ activities: any[] }>(`${url}${query}`, {}, !!ACTIVITIES_URL);
  }

  async createActivity(activity: any) {
    const ACTIVITIES_URL = import.meta.env.VITE_ADMIN_ACTIVITIES_URL || "";
    const url = ACTIVITIES_URL || `${this.baseUrl}/admin/activities`;
    return this.request<{ activity: any }>(
      url,
      {
        method: "POST",
        body: JSON.stringify(activity),
      },
      !!ACTIVITIES_URL
    );
  }

  async updateActivity(id: string, activity: any) {
    const ACTIVITIES_URL = import.meta.env.VITE_ADMIN_ACTIVITIES_URL || "";
    const baseUrl = ACTIVITIES_URL || `${this.baseUrl}/admin/activities`;
    const url = `${baseUrl}?id=${encodeURIComponent(id)}`;
    return this.request<{ activity: any }>(
      url,
      {
        method: "PUT",
        body: JSON.stringify({ ...activity, id }),
      },
      !!ACTIVITIES_URL
    );
  }

  async deleteActivity(id: string) {
    const ACTIVITIES_URL = import.meta.env.VITE_ADMIN_ACTIVITIES_URL || "";
    const baseUrl = ACTIVITIES_URL || `${this.baseUrl}/admin/activities`;
    const url = `${baseUrl}?id=${encodeURIComponent(id)}`;
    return this.request<{ message: string; id: string }>(
      url,
      {
        method: "DELETE",
      },
      !!ACTIVITIES_URL
    );
  }
  async getAIUsage() {
    const AI_USAGE_URL = import.meta.env.VITE_ADMIN_GET_AI_USAGE_URL || "";
    // If VITE_ADMIN_GET_AI_USAGE_URL is a full URL (which it likely is based on other env vars), use it directly.
    // Otherwise construct it.
    // The existing pattern suggests VITE_ADMIN_..._URL variables are full URLs.

    // However, the current implementation in AIUsagePage.tsx uses VITE_ADMIN_GET_AI_USAGE_URL directly.
    // adminApi.request handles full URLs if the third argument is true.

    return this.request<{
      logs: any[],
      stats: {
        totalCost: number,
        totalTokens: number,
        requestCount: number,
        uniqueUsers: number,
        costByModel: Record<string, number>
      }
    }>(AI_USAGE_URL, {}, !!AI_USAGE_URL);
  }
}

export const adminApi = new AdminApiClient(ADMIN_API_BASE_URL);
